import { useState, useCallback, useEffect } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { AppIcon } from "@/components/AppIcon";
import { useNativeApp } from "@/hooks/useNativeApp";

const slides = [
  {
    type: "welcome" as const,
    title: "Your AI Kitchen Assistant",
    description: "Smart recipes, meal planning, and pantry management — all in one app.",
  },
  {
    type: "feature" as const,
    image: "/images/landing/ai-recipe-generation.webp",
    title: "AI Recipe Generation",
    description: "Generate personalized recipes with AI based on your ingredients and preferences.",
  },
  {
    type: "feature" as const,
    image: "/images/landing/meal-planning.webp",
    title: "Meal Planning & Pantry",
    description: "Plan your week, track your pantry, and create smart grocery lists automatically.",
  },
  {
    type: "cta" as const,
    title: "Ready to Cook Smarter?",
    description: "Create your free account and start your culinary journey.",
  },
];

export const MobileOnboarding = () => {
  const navigate = useNavigate();
  const { isNativeApp } = useNativeApp();
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    return () => { emblaApi.off("select", onSelect); };
  }, [emblaApi, onSelect]);

  const isLast = selectedIndex === slides.length - 1;

  return (
    <main className="fixed inset-0 bg-background flex flex-col">
      {/* Skip */}
      {!isLast && (
        <div className="absolute top-4 right-4 z-10">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/auth")}
            className="text-muted-foreground"
          >
            Skip
          </Button>
        </div>
      )}

      {/* Carousel */}
      <div className="flex-1 overflow-hidden" ref={emblaRef}>
        <div className="flex h-full">
          {slides.map((slide, i) => (
            <div
              key={i}
              className="flex-[0_0_100%] min-w-0 flex flex-col items-center justify-center px-8 text-center"
            >
              {slide.type === "welcome" && (
                <>
                  <AppIcon size={100} className="mb-8" />
                  <h1 className="text-3xl font-bold text-foreground mb-4">{slide.title}</h1>
                  <p className="text-lg text-muted-foreground max-w-sm">{slide.description}</p>
                </>
              )}

              {slide.type === "feature" && (
                <>
                  <div className="w-full max-w-xs h-48 rounded-2xl overflow-hidden shadow-lg mb-8">
                    <img
                      src={slide.image}
                      alt={slide.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <h2 className="text-2xl font-bold text-foreground mb-3">{slide.title}</h2>
                  <p className="text-base text-muted-foreground max-w-sm">{slide.description}</p>
                </>
              )}

              {slide.type === "cta" && (
                <>
                  <AppIcon size={80} className="mb-6" />
                  <h2 className="text-2xl font-bold text-foreground mb-3">{slide.title}</h2>
                  <p className="text-base text-muted-foreground max-w-sm mb-8">{slide.description}</p>
                  <Button
                    size="lg"
                    onClick={() => navigate("/auth")}
                    className="rounded-full px-10 py-4 text-lg"
                  >
                    Get Started
                  </Button>
                  {!isNativeApp && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate("/pricing")}
                      className="mt-3 text-muted-foreground"
                    >
                      See Plans
                    </Button>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Dots */}
      <div className="flex justify-center gap-2 pb-12">
        {slides.map((_, i) => (
          <div
            key={i}
            className={`w-2.5 h-2.5 rounded-full transition-all ${
              i === selectedIndex
                ? "bg-primary w-6"
                : "bg-muted-foreground/30"
            }`}
          />
        ))}
      </div>
    </main>
  );
};
